import React, {forwardRef, useId} from 'react';
import Select from 'react-select';

import {useTheme} from '@spothero/ui';

import MultiselectControl from './MultiselectControl';
import MultiselectDropdownIndicator from './MultiselectDropdownIndicator';
import MultiselectInput from './MultiselectInput';
import MultiselectNoOptionsMessage from './MultiselectNoOptionsMessage';
import {type SelectProps} from './multiselect.types';
import {generateCustomStylesAndTheme, type ThemeArguments} from './utils';

/**
 * A customizable multiselect component based off of the `react-select` package.
 * See {@link https://react-select.com/props `react-select` documentation} for more details on the available props and customization options.
 *
 * @param {SelectProps} props - The props for the multiselect component.
 */
const Multiselect = forwardRef<never, SelectProps>(
    (
        {
            closeMenuOnSelect = false,
            hideSelectedOptions = false,
            isMulti = true,
            ...props
        },
        ref
    ) => {
        const generatedId = useId();
        const inputId = props.inputId || generatedId;
        const instanceId = props.instanceId || generatedId;
        const {
            colors,
            fontSizes,
            fontWeights,
            sizes,
            radii,
            zIndices,
        }: ThemeArguments = useTheme();
        const {customTheme, customStyles} = generateCustomStylesAndTheme({
            colors,
            fontSizes,
            fontWeights,
            sizes,
            radii,
            zIndices,
        });

        return (
            <Select
                ref={ref}
                styles={customStyles}
                theme={customTheme}
                components={{
                    Control: MultiselectControl,
                    IndicatorSeparator: null,
                    ClearIndicator: null,
                    Input: MultiselectInput,
                    DropdownIndicator: MultiselectDropdownIndicator,
                    NoOptionsMessage: MultiselectNoOptionsMessage,
                }}
                isMulti={isMulti}
                hideSelectedOptions={hideSelectedOptions}
                closeMenuOnSelect={closeMenuOnSelect}
                inputId={inputId}
                instanceId={instanceId}
                {...props}
            />
        );
    }
);

export default Multiselect;
